import { Plus } from 'lucide-react';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { CommandGroup, CommandItem } from '@heidi/design-system/command';
import { TypographyP2, TypographyP2Bold } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';

interface DropdownFooterProps {
  /** Trimmed typed query — drives the badge label and conditional rename row. */
  trimmedQuery: string;
  /** cmdk value for the Create row. */
  createValue: string;
  /** cmdk value for the Create-unlinked-session row. */
  renameValue: string;
  /** Single dispatcher for both rows; routes by value. */
  onSelect: (value: string) => void;
  /**
   * Divider side for the group container: `border-t` (default) when rendered
   * at the bottom of the dropdown, `border-b` when rendered at the top (the
   * session-merge layout, above the suggested list).
   */
  className?: string;
}

/**
 * Create-action group of the find-or-link dropdown. Sits outside
 * `<CommandList>` so arrow navigation stays within the result rows;
 * Enter-on-empty fires the Create row from the combobox instead. Placed at the
 * bottom by default; the session-merge layout renders it at the top (pass
 * `className="border-b"`).
 *
 * Layout:
 * - Create new patient (primary, always shown) with the typed name as a
 *   dark pill on the right when present.
 * - Create unlinked session (tertiary, only shown when the user has
 *   typed something — empty session creation flows through the legacy
 *   `LinkSessionsToPatientModal` from the Create row instead).
 */
export const DropdownFooter: React.FC<DropdownFooterProps> = ({
  trimmedQuery,
  createValue,
  renameValue,
  onSelect,
  className = 'border-t',
}) => (
  <CommandGroup className={cn('border-border p-2', className)}>
    <CommandItem
      data-testid="create-new-patient-row"
      value={createValue}
      onSelect={onSelect}
      className="flex min-h-9 w-full items-center justify-between gap-x-3 rounded-md px-2 py-1.5 data-[selected=true]:bg-surface-accent"
    >
      <TypographyP2Bold className="text-text-primary">
        <FormattedMessage
          defaultMessage="Create new patient"
          id="hNmxmtIPl2"
          description="Primary action in the patient search dropdown to create a new patient record"
        />
      </TypographyP2Bold>
      {trimmedQuery && (
        <span className="inline-flex shrink-0 items-center gap-x-1.5 rounded-md bg-background-button-primary px-2.5 py-1 text-text-primary-invert">
          <Plus className="size-3.5" />
          <TypographyP2Bold className="text-text-primary-invert">
            {trimmedQuery}
          </TypographyP2Bold>
        </span>
      )}
    </CommandItem>
    {trimmedQuery && (
      <CommandItem
        value={renameValue}
        onSelect={onSelect}
        className="flex min-h-9 w-full items-center justify-start rounded-md px-2 py-1.5 data-[selected=true]:bg-surface-accent"
      >
        <TypographyP2 className="text-text-tertiary">
          <FormattedMessage
            defaultMessage="Create unlinked session"
            id="e+zh5QPlmW"
            description="Secondary action in the patient search dropdown to create a session without linking to a patient"
          />
        </TypographyP2>
      </CommandItem>
    )}
  </CommandGroup>
);
